<template>
  <Card :title="t('password.form.title')">
    <form class="form" novalidate @submit.prevent="handleSubmit">
        <FormErrorsList
          v-if="changePasswordResponse && !changePasswordResponse.succeeded && changePasswordResponse.errors.length > 0"
          :title="t('password.form.failedMessage')"
          :errorsList="errorsMessages"
          />
        <FormTooltip>
          <p>{{t('password.form.format')}}</p>
        </FormTooltip>
        <FormInput ref="newPasswordInput" v-model="newPassword" :label="t('password.form.newPassword')"
                    name="newPassword" type="password" @validated="handleValidation" />
        <FormInput ref="newPasswordConfirmationInput" v-model="newPasswordConfirmation" :label="t('password.form.newPasswordConfirmation')"
                    name="newPasswordConfirmation" type="password" @validated="handleValidation" />
      <button class="form__submit btn btn--fullscreen">{{ t('global.save') }}</button>
    </form>
  </Card>
</template>

<script lang="ts" setup>
import {computed, ref} from "vue";

import Card from "@/components/layout/Card.vue";
import FormInput from "@/components/forms/FormInput.vue";
import FormErrorsList from "@/components/forms/FormErrorsList.vue";

import {useI18n} from "vue3-i18n";
import {useUserService} from "@/inversify.config";
import {SucceededOrNotResponse} from "@/types/responses/succeededOrNotResponse";
import { notifyError, notifySuccess } from "@/notify";
import { Status } from "@/validation";
import {IResetMemberPasswordRequest} from "@/types/requests";
import FormTooltip from "@/components/forms/FormTooltip.vue";

// eslint-disable-next-line no-undef
const props = defineProps<{
  organizationId: string;
  memberId: string;
}>();

const {t} = useI18n()
const userService = useUserService()

const currentPassword = ref<string>()
const currentPasswordInput = ref<typeof FormInput>()

const newPassword = ref<string>()
const newPasswordInput = ref<typeof FormInput>()

const newPasswordConfirmation = ref<string>()
const newPasswordConfirmationInput = ref<typeof FormInput>()

const changePasswordResponse = ref<SucceededOrNotResponse>()
let errorsMessages = computed(() => {
  if (!changePasswordResponse.value)
    return []
  let response = changePasswordResponse.value as SucceededOrNotResponse
  return response.getErrorMessages('validation.password')
})

const inputValidationStatuses:any = {};
async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid
}

async function showErrorMessages() {
  currentPasswordInput.value?.validateInput(currentPassword.value);
  newPasswordInput.value?.validateInput(newPassword.value);
  newPasswordConfirmationInput.value?.validateInput(newPasswordConfirmation.value);
}

async function handleSubmit() {
  await showErrorMessages();

  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  }

  const request = {
    newPassword: newPassword.value,
    newPasswordConfirmation: newPasswordConfirmation.value,
  } as IResetMemberPasswordRequest

  changePasswordResponse.value = await userService
      .resetMemberPassword(props.organizationId, props.memberId, request) as SucceededOrNotResponse;

  changePasswordResponse.value.succeeded
    ? notifySuccess(t('password.form.successMessage'))
    : notifyError(t('password.form.failedMessage'));
}
</script>
